import React, { useState } from "react";
import { X as XIcon, Link2, Copy, Check } from "lucide-react";
import { useModalHistory } from "../utils/historyNav";

const enc = encodeURIComponent;

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch (e2) {
      return false;
    }
  }
}

const TelegramGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="white" aria-hidden="true">
    <path d="M9.04 15.31l-.38 5.37c.55 0 .79-.24 1.08-.52l2.6-2.49 5.39 3.95c.99.55 1.7.26 1.97-.92l3.57-16.73c.35-1.44-.52-2.01-1.48-1.65L1.28 9.87c-1.42.55-1.4 1.34-.24 1.7l5.32 1.66L18.7 5.5c.58-.38 1.11-.17.67.21L9.04 15.31z" />
  </svg>
);

const WhatsAppGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="white" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export const ShareServiceModal = ({ user, onClose, showToast }) => {
  useModalHistory(true, onClose);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedText, setCopiedText] = useState(false);

  const isExecutor = user?.role === "executor";
  const campaign = isExecutor ? "supply_share" : "demand_share";
  const path = isExecutor ? "/krasnodar/podrabotka.html" : "/";
  const link = `${window.location.origin}${path}?utm_source=share&utm_medium=referral&utm_campaign=${campaign}`;

  const msgBase = isExecutor
    ? "Подрабатываю на Реверсии: поручения рядом с домом, оплата после каждого задания. В Краснодаре пока мало исполнителей — заказы разбирают быстро. Регистрация занимает 2 минуты."
    : "Реверсия — соседи выполняют поручения в Краснодаре: забрать, проверить, сфотографировать. Цена видна заранее, оплата защищена до результата.";
  const fullText = `${msgBase} ${link}`;

  const shareTargets = [
    {
      id: "telegram",
      label: "Telegram",
      href: `https://t.me/share/url?url=${enc(link)}&text=${enc(msgBase)}`,
      badgeBg: "#229ED9",
      glyph: <TelegramGlyph />,
    },
    {
      id: "max",
      label: "MAX",
      href: `https://max.ru/:share?text=${enc(fullText)}`,
      badgeBg: "linear-gradient(135deg, #6E7BF2 0%, #8A5CF6 100%)",
      glyph: <span style={{ color: "white", fontSize: "0.55rem", fontWeight: 800, letterSpacing: "0.02em", fontFamily: "Manrope,sans-serif" }}>MAX</span>,
    },
    {
      id: "vk",
      label: "VK",
      href: `https://vk.com/share.php?url=${enc(link)}&title=${enc("Реверсия — задания и подработка рядом")}&comment=${enc(msgBase)}`,
      badgeBg: "#0077FF",
      glyph: <span style={{ color: "white", fontSize: "0.65rem", fontWeight: 800, fontFamily: "Manrope,sans-serif" }}>VK</span>,
    },
    {
      id: "whatsapp",
      label: "WhatsApp",
      href: `https://wa.me/?text=${enc(fullText)}`,
      badgeBg: "#25D366",
      glyph: <WhatsAppGlyph />,
    },
  ];

  const handleCopyLink = async () => {
    const ok = await copyToClipboard(link);
    if (ok) {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
      showToast && showToast("Ссылка скопирована", "success", 4000);
    } else {
      showToast && showToast("Не удалось скопировать. Выделите ссылку вручную.", "error", 6000);
    }
  };

  const handleCopyText = async () => {
    const ok = await copyToClipboard(fullText);
    if (ok) {
      setCopiedText(true);
      setTimeout(() => setCopiedText(false), 2000);
      showToast && showToast("Текст скопирован — вставьте его в любой чат", "success", 4000);
    } else {
      showToast && showToast("Не удалось скопировать. Выделите текст вручную.", "error", 6000);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ zIndex: 10000 }}
      onClick={onClose}
      data-testid="share-modal-overlay"
    >
      <div
        data-testid="share-modal"
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl overflow-y-auto"
        style={{ maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 pt-5 pb-1">
          <div>
            <h3 className="text-lg font-bold" style={{ fontFamily: "Manrope,sans-serif", color: "var(--rv-text-primary)" }}>
              Рассказать о Реверсии
            </h3>
            <p className="text-sm mt-0.5" style={{ color: "var(--rv-text-muted)", fontFamily: "IBM Plex Sans,sans-serif" }}>
              {isExecutor
                ? "Знакомым пригодится подработка рядом с домом"
                : "Отправьте сервис тем, кому нужна помощь с делами"}
            </p>
          </div>
          <button
            data-testid="share-modal-close-btn"
            onClick={onClose}
            className="p-1.5 rounded-lg flex-shrink-0 ml-3"
            style={{ background: "var(--rv-paper)", border: "none", cursor: "pointer", color: "var(--rv-text-secondary)" }}
            aria-label="Закрыть"
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="px-5 pt-4 pb-5 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {shareTargets.map((t) => (
              <a
                key={t.id}
                data-testid={`share-btn-${t.id}`}
                href={t.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex sm:flex-col items-center gap-2 px-3 py-2.5 sm:py-3 rounded-xl transition-colors"
                style={{
                  border: "1px solid var(--rv-border)",
                  textDecoration: "none",
                  color: "var(--rv-text-primary)",
                  fontFamily: "IBM Plex Sans,sans-serif",
                }}
              >
                <span
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: t.badgeBg }}
                >
                  {t.glyph}
                </span>
                <span className="text-sm font-medium">{t.label}</span>
              </a>
            ))}
          </div>

          <button
            data-testid="share-copy-link-btn"
            onClick={handleCopyLink}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors"
            style={{
              background: "white",
              border: "1px solid var(--rv-border)",
              color: "var(--rv-text-secondary)",
              cursor: "pointer",
              fontFamily: "IBM Plex Sans,sans-serif",
            }}
          >
            {copiedLink ? <Check size={16} style={{ color: "var(--rv-teal)" }} /> : <Link2 size={16} />}
            {copiedLink ? "Ссылка скопирована" : "Скопировать ссылку"}
          </button>

          <div className="rounded-xl p-3" style={{ background: "var(--rv-paper)", border: "1px solid var(--rv-border)" }}>
            <p className="text-xs font-medium mb-1.5" style={{ color: "var(--rv-text-muted)", fontFamily: "IBM Plex Sans,sans-serif" }}>
              Готовый текст для чата или сообщения
            </p>
            <p data-testid="share-ready-text" className="text-sm leading-relaxed" style={{ color: "var(--rv-text-primary)", fontFamily: "IBM Plex Sans,sans-serif", wordBreak: "break-word" }}>
              {fullText}
            </p>
            <button
              data-testid="share-copy-text-btn"
              onClick={handleCopyText}
              className="mt-2.5 w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
              style={{
                background: copiedText ? "var(--rv-teal-dark, #0f766e)" : "var(--rv-teal)",
                color: "white",
                border: "none",
                cursor: "pointer",
                fontFamily: "IBM Plex Sans,sans-serif",
              }}
            >
              {copiedText ? <Check size={16} /> : <Copy size={16} />}
              {copiedText ? "Текст скопирован" : "Скопировать текст"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
